import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { TrendingDown, Plus, Trash2, ArrowRight, Sparkles } from "lucide-react";
import { toast } from 'sonner';
import { UniversalSymptomTracker } from '@/components/mental/UniversalSymptomTracker';
import { useUniversalTracking } from '@/hooks/useUniversalTracking';

interface HierarchyItem {
  id: string;
  situation: string;
  anxietyRating: number;
  notes: string;
}

interface ExposureHierarchyProps {
  userId: string;
  onBack: () => void;
  onComplete?: () => void;
}

type ExerciseStep = 'intro' | 'symptoms-before' | 'hierarchy' | 'symptoms-after' | 'complete';

export const ExposureHierarchy = ({ userId, onBack, onComplete }: ExposureHierarchyProps) => {
  const [exerciseStep, setExerciseStep] = useState<ExerciseStep>('intro');
  const [items, setItems] = useState<HierarchyItem[]>([]);
  const [newSituation, setNewSituation] = useState('');
  const [newRating, setNewRating] = useState([50]);
  const [symptomsBefore, setSymptomsBefore] = useState<Record<string, number>>({});
  const [symptomsAfter, setSymptomsAfter] = useState<Record<string, number>>({});
  const { logExercise, isLogging } = useUniversalTracking();

  const addItem = () => {
    if (!newSituation.trim()) {
      toast.error('Please describe the situation');
      return;
    }

    const newItem: HierarchyItem = {
      id: crypto.randomUUID(),
      situation: newSituation,
      anxietyRating: newRating[0],
      notes: ''
    };

    setItems([...items, newItem].sort((a, b) => a.anxietyRating - b.anxietyRating));
    setNewSituation('');
    setNewRating([50]);
    toast.success('Item added to hierarchy');
  };

  const removeItem = (id: string) => {
    setItems(items.filter(item => item.id !== id));
  };

  const handleComplete = async () => {
    const success = await logExercise({
      exercise_type: 'exposure_hierarchy',
      exercise_name: 'Exposure Hierarchy Builder',
      category: 'anxiety',
      duration_minutes: 15,
      completion_data: {
        total_items: items.length,
        lowest_rating: items.length > 0 ? Math.min(...items.map(i => i.anxietyRating)) : 0,
        highest_rating: items.length > 0 ? Math.max(...items.map(i => i.anxietyRating)) : 0,
        items: items.map(i => ({ situation: i.situation, rating: i.anxietyRating }))
      },
      symptoms_before: symptomsBefore,
      symptoms_after: symptomsAfter,
    });

    if (success) {
      setExerciseStep('complete');
    }
  };

  const getRatingColor = (rating: number) => {
    if (rating < 40) return 'bg-green-500/20 text-green-700 dark:text-green-400';
    if (rating < 70) return 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400';
    return 'bg-red-500/20 text-red-700 dark:text-red-400';
  };

  if (exerciseStep === 'intro') {
    return (
      <div className="space-y-6 animate-fade-in">
        <Button variant="outline" onClick={onBack}>← Back to Anxiety Tools</Button>
        <Card className="bg-gradient-therapeutic border-primary/20">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-primary/10">
                <TrendingDown className="h-7 w-7 text-primary" />
              </div>
              <div>
                <CardTitle className="text-2xl">Exposure Hierarchy Builder</CardTitle>
                <p className="text-base text-foreground/80 mt-1">
                  Create a gradual plan to face your fears
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              This evidence-based technique helps you overcome anxiety by gradually facing 
              feared situations, starting with the least anxiety-provoking.
            </p>
            <Button onClick={() => setExerciseStep('symptoms-before')} className="w-full gap-2">
              Begin Building <ArrowRight className="w-4 h-4" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (exerciseStep === 'symptoms-before') {
    return (
      <div className="space-y-6 animate-fade-in">
        <Button variant="outline" onClick={() => setExerciseStep('intro')}>← Back</Button>
        <UniversalSymptomTracker mode="before" onRatingsChange={setSymptomsBefore} />
        <Button 
          onClick={() => setExerciseStep('hierarchy')} 
          className="w-full"
          disabled={Object.keys(symptomsBefore).length === 0}
        >
          Continue to Hierarchy Builder
        </Button>
      </div>
    );
  }

  if (exerciseStep === 'symptoms-after') {
    return (
      <div className="space-y-6 animate-fade-in">
        <UniversalSymptomTracker mode="after" previousRatings={symptomsBefore} onRatingsChange={setSymptomsAfter} />
        <Button 
          onClick={handleComplete} 
          className="w-full"
          disabled={Object.keys(symptomsAfter).length === 0 || isLogging}
        >
          {isLogging ? 'Saving...' : 'Complete Session'}
        </Button>
      </div>
    );
  }

  if (exerciseStep === 'complete') {
    return (
      <div className="space-y-6 animate-fade-in">
        <Card className="bg-gradient-to-br from-accent/20 to-primary/10 border-accent/30">
          <CardContent className="pt-6 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mx-auto">
              <Sparkles className="w-8 h-8 text-accent" />
            </div>
            <h3 className="text-xl font-semibold">Hierarchy Saved!</h3>
            <p className="text-muted-foreground">
              Start with items rated 40-60, practice until anxiety reduces by half, then move up.
            </p>
            <Button onClick={onComplete || onBack} className="w-full">Finish</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <Button variant="outline" onClick={() => setExerciseStep('symptoms-before')}>← Back</Button>

      <Card className="bg-gradient-therapeutic border-primary/20">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-full bg-primary/10">
              <TrendingDown className="h-7 w-7 text-primary" />
            </div>
            <CardTitle className="text-2xl">Exposure Hierarchy Builder</CardTitle>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          <Card className="bg-gradient-calm border-border/50">
            <CardContent className="pt-6 space-y-4">
              <div className="space-y-2">
                <label className="text-base font-medium">Situation or Activity</label>
                <Textarea
                  value={newSituation}
                  onChange={(e) => setNewSituation(e.target.value)}
                  placeholder="E.g., 'Give a presentation at work'"
                  className="min-h-[80px]"
                />
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="text-base font-medium">Anxiety Rating (0-100)</label>
                  <Badge variant="secondary">{newRating[0]}</Badge>
                </div>
                <Slider value={newRating} onValueChange={setNewRating} max={100} step={5} />
                <p className="text-sm text-muted-foreground">Start with items rated 40-60</p>
              </div>

              <Button onClick={addItem} className="w-full">
                <Plus className="mr-2 h-5 w-5" /> Add to Hierarchy
              </Button>
            </CardContent>
          </Card>

          {items.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-lg font-medium">Your Exposure Hierarchy</h3>
              {items.map((item, index) => (
                <Card key={item.id} className="border-border/50">
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{index + 1}</Badge>
                          <Badge className={getRatingColor(item.anxietyRating)}>{item.anxietyRating}/100</Badge>
                        </div>
                        <p>{item.situation}</p>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removeItem(item.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {items.length === 0 && (
            <Card className="bg-muted/50 border-dashed">
              <CardContent className="pt-6 text-center text-muted-foreground">
                <p>Add situations to build your hierarchy</p>
                <p className="text-sm mt-2">Aim for 8-10 situations ranging from mild to severe</p>
              </CardContent>
            </Card>
          )}

          <Button 
            onClick={() => setExerciseStep('symptoms-after')} 
            className="w-full"
            disabled={items.length === 0}
          >
            Finish & Rate How You Feel
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
